import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useTranslation} from 'react-i18next'
import {useSnackBar} from '@hooks/alert/useSnackBar'
import {useAppContext} from '@context/appContext/hooks/useAppContext'
import {useGetRelatedAccountsQuery} from '@containers/account/hooks/useAccountsQuery'
import {useGetUserAccessWidthState} from '@containers/account/access/hooks/useGetUserAccess'
import {
	applyDashboardSettingsApi,
	getDashboardInfoApi,
	getDashboardNewsApi,
	getDashboardSessionsApi,
	getVoyagesRealtimeApi,
} from '@containers/dashboard/api/api'
import {IBodyDashboardSettings, IParamsTrackTablePage} from '@containers/dashboard/types'

const QUERY_KEY_DASHBOARD_INFO = 'dashboard-info'
const QUERY_KEY_DASHBOARD_SESSIONS = 'dashboard-sessions'
const QUERY_KEY_DASHBOARD_NEWS = 'dashboard-news'
const QUERY_KEY_DASHBOARD_VOYAGES = 'dashboard-voyages'

export const useDashboardInfoQuery = () => {
	return useQuery({
		queryFn: getDashboardInfoApi,
		queryKey: [QUERY_KEY_DASHBOARD_INFO],
		staleTime: 1000 * 60,
	})
}

export const useDashboardSessionsQuery = (params: IParamsTrackTablePage) => {
	return useQuery({
		queryFn: () => getDashboardSessionsApi(params),
		queryKey: [QUERY_KEY_DASHBOARD_SESSIONS, params],
		placeholderData: keepPreviousData,
	})
}

export const useDashboardNewsQuery = (params: IParamsTrackTablePage) => {
	return useQuery({
		queryFn: () => getDashboardNewsApi(params),
		queryKey: [QUERY_KEY_DASHBOARD_NEWS, params],
		placeholderData: keepPreviousData,
		staleTime: 1000 * 60 * 10,
	})
}

export const useApplyDashboardSettingsMutation = () => {
	const queryClient = useQueryClient()
	const {setSnackBar} = useSnackBar()
	const {t} = useTranslation()

	return useMutation({
		mutationFn: (body: IBodyDashboardSettings) => applyDashboardSettingsApi(body),
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [QUERY_KEY_DASHBOARD_INFO]})
			setSnackBar({title: t('dashboardPage.saved'), variant: 'info'})
		},
		onError: () => {
			setSnackBar({message: t('dashboardPage.saveError'), variant: 'error'})
		},
	})
}

// Same accounts as the statistic page: the multi-account sums its carriers.
export const useDashboardVoyagesQuery = () => {
	const {account, isMultiAccount} = useAppContext()
	const {access} = useGetUserAccessWidthState({keys: 'acl.voyage.READ'})

	const {data: accounts} = useGetRelatedAccountsQuery({
		params: {accountId: account?.id},
		enabled: access && isMultiAccount,
	})

	const accountIds = isMultiAccount
		? accounts?.map((item) => item.id).join(',')
		: account?.id?.toString()

	return useQuery({
		queryFn: () => getVoyagesRealtimeApi(accountIds),
		queryKey: [QUERY_KEY_DASHBOARD_VOYAGES, accountIds],
		enabled: access && !!accountIds,
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
	})
}
